// Sürücü Puantajı — ana sayfa.
// Sürücülerin çalışma performansını (bir önceki gün, son 7 gün ve seçilen tarih aralığında servis sayısı, kilometre ve süre), önümüzdeki
// 7 günlük programı ve sürücü başına aylık ayrıntılı dökümü gösterir. Salt-okunur.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import Button from "shared/components/FormElements/Button";

import useDriverPerformanceApi from "./hooks/useDriverPerformanceApi";
import DriverStatsTable from "./components/DriverStatsTable";
import RestFleetTable from "./components/RestFleetTable";
import UpcomingAgenda from "./components/UpcomingAgenda";
import DriverPerformanceDetailModal from "./DriverPerformanceDetailModal";
import DriverRestDetailModal from "./DriverRestDetailModal";
import {
  applyDriverFilters,
  clampDateRange,
  daysInRange,
  loadStoredFilters,
  mapRowToDateRange,
  saveStoredFilters,
} from "./performanceFilters";
import {
  KIND_META,
  KIND_ORDER,
  addDays,
  exportPerformanceExcel,
  formatKm,
  formatMinutes,
  formatNumber,
  todayYMD,
} from "./utils";

import "./DriverPerformance.css";

const DriverPerformance = () => {
  const { t } = useTranslation();
  const { isLoading, getPerformance, getRestFleet } = useDriverPerformanceApi();

  const yesterday = addDays(todayYMD(), -1);
  const initialFilters = useMemo(() => loadStoredFilters(), []);

  const [rangeEnd, setRangeEnd] = useState(initialFilters.rangeEnd);
  const [rangeStart, setRangeStart] = useState(initialFilters.rangeStart);
  const [nameQuery, setNameQuery] = useState(initialFilters.nameQuery);
  const [metricsMissingOnly, setMetricsMissingOnly] = useState(initialFilters.metricsMissingOnly);
  const [activeTab, setActiveTab] = useState(initialFilters.activeTab);

  const [data, setData] = useState(null);
  const [restData, setRestData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [restErrorMessage, setRestErrorMessage] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedRestDriver, setSelectedRestDriver] = useState(null);
  const [restLoading, setRestLoading] = useState(false);
  const restAsOfRef = useRef(null);

  useEffect(() => {
    saveStoredFilters({ rangeStart, rangeEnd, nameQuery, metricsMissingOnly, activeTab });
  }, [rangeStart, rangeEnd, nameQuery, metricsMissingOnly, activeTab]);

  const fetchAllData = useCallback(async () => {
    setErrorMessage(null);
    setRestErrorMessage(null);

    const hasCachedRest = restAsOfRef.current === rangeEnd;
    const fetchRest = !hasCachedRest;

    if (fetchRest) {
      setRestLoading(true);
      setRestData(null);
    }

    const perfPromise = getPerformance(rangeEnd);
    const restPromise = fetchRest ? getRestFleet(rangeEnd) : null;

    try {
      if (restPromise) {
        const [perfResult, restResult] = await Promise.allSettled([perfPromise, restPromise]);

        if (perfResult.status === "fulfilled") {
          setData(perfResult.value);
        } else {
          setData(null);
          setErrorMessage(perfResult.reason?.message || t("driverPerformance.fetchError"));
        }

        if (restResult.status === "fulfilled") {
          setRestData(restResult.value);
          restAsOfRef.current = rangeEnd;
        } else {
          setRestData(null);
          restAsOfRef.current = null;
          setRestErrorMessage(restResult.reason?.message || t("driverPerformance.rest.fetchError"));
        }
      } else {
        const perfResult = await perfPromise;
        setData(perfResult);
      }
    } catch (err) {
      setErrorMessage(err?.message || t("driverPerformance.fetchError"));
    } finally {
      if (fetchRest) {
        setRestLoading(false);
      }
    }
  }, [rangeEnd, getPerformance, getRestFleet, t]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const rows = data?.drivers || [];

  const transformedRows = useMemo(
    () => rows.map((r) => mapRowToDateRange(r, rangeStart, rangeEnd)),
    [rows, rangeStart, rangeEnd],
  );

  const performanceFilterOpts = useMemo(() => ({ nameQuery, metricsMissingOnly }), [nameQuery, metricsMissingOnly]);

  const nameFilterOpts = useMemo(() => ({ nameQuery, metricsMissingOnly: false }), [nameQuery]);

  const activePerformanceRows = useMemo(
    () =>
      applyDriverFilters(
        transformedRows.filter((r) => r.driver.isActive),
        performanceFilterOpts,
      ),
    [transformedRows, performanceFilterOpts],
  );

  const passivePerformanceRows = useMemo(
    () =>
      applyDriverFilters(
        transformedRows.filter((r) => !r.driver.isActive),
        performanceFilterOpts,
      ),
    [transformedRows, performanceFilterOpts],
  );

  const exportRows = useMemo(
    () => applyDriverFilters(transformedRows, performanceFilterOpts),
    [transformedRows, performanceFilterOpts],
  );

  const restRows = restData?.drivers || [];

  const restActiveRows = useMemo(
    () =>
      applyDriverFilters(
        restRows.filter((r) => r.driver.isActive),
        nameFilterOpts,
      ),
    [restRows, nameFilterOpts],
  );

  const restPassiveRows = useMemo(
    () =>
      applyDriverFilters(
        restRows.filter((r) => !r.driver.isActive),
        nameFilterOpts,
      ),
    [restRows, nameFilterOpts],
  );

  const upcomingActiveRows = useMemo(
    () =>
      applyDriverFilters(
        rows.filter((r) => r.driver.isActive),
        nameFilterOpts,
      ),
    [rows, nameFilterOpts],
  );

  const avgActiveMonth = useMemo(() => {
    if (!activePerformanceRows.length) return { count: 0 };
    const total = activePerformanceRows.reduce((acc, r) => acc + r.metrics.month.count, 0);
    return { count: total / activePerformanceRows.length };
  }, [activePerformanceRows]);

  const summary = useMemo(() => {
    const filteredAll = [...activePerformanceRows, ...passivePerformanceRows];
    return {
      drivers: filteredAll.length,
      activeDrivers: activePerformanceRows.length,
      monthServices: filteredAll.reduce((acc, r) => acc + r.metrics.month.count, 0),
      monthKm: filteredAll.reduce((acc, r) => acc + r.metrics.month.km, 0),
      monthMinutes: filteredAll.reduce((acc, r) => acc + r.metrics.month.minutes, 0),
    };
  }, [activePerformanceRows, passivePerformanceRows]);

  const filteredRestStats = useMemo(() => {
    const all = [...restActiveRows, ...restPassiveRows];
    return {
      driversWithViolations: all.filter((r) => (r.violationCount || 0) > 0).length,
      totalViolations: all.reduce((acc, r) => acc + (r.violationCount || 0), 0),
    };
  }, [restActiveRows, restPassiveRows]);

  const periodDays = useMemo(() => daysInRange(rangeStart, rangeEnd), [rangeStart, rangeEnd]);

  const handleExport = useCallback(() => {
    if (exportRows.length) {
      exportPerformanceExcel(exportRows, t, { rangeStart, rangeEnd, periodDays });
    }
  }, [exportRows, t, rangeStart, rangeEnd, periodDays]);

  const handleRangeEndChange = useCallback(
    (value) => {
      let end = value || yesterday;
      if (end > yesterday) end = yesterday;
      const clamped = clampDateRange(rangeStart, end);
      setRangeEnd(clamped.rangeEnd);
      setRangeStart(clamped.rangeStart);
    },
    [rangeStart, yesterday],
  );

  const handleRangeStartChange = useCallback(
    (value) => {
      if (!value) return;
      const clamped = clampDateRange(value, rangeEnd);
      setRangeStart(clamped.rangeStart);
    },
    [rangeEnd],
  );

  const minRangeStart = addDays(rangeEnd, -29);
  const showDateFilters = activeTab === "performance" || activeTab === "rest";

  const detailRange = useMemo(
    () =>
      activeTab === "upcoming"
        ? { start: todayYMD(), end: addDays(todayYMD(), 6), mode: "upcoming", daysSortOrder: "asc" }
        : { start: rangeStart, end: rangeEnd, mode: "performance", daysSortOrder: "desc" },
    [activeTab, rangeStart, rangeEnd],
  );

  const restDetailRange = useMemo(() => ({ start: rangeStart, end: rangeEnd }), [rangeStart, rangeEnd]);

  return (
    <div className="dp-page">
      <div className="dp-page__header">
        <h2>{t("driverPerformance.title")}</h2>
        <div className="dp-page__controls">
          <Button type="button" size="small" disabled={!exportRows.length} onClick={handleExport}>
            {t("driverPerformance.exportExcel")}
          </Button>
        </div>
      </div>

      <div className="dp-page__filters">
        <input
          type="search"
          className="dp-page__search"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder={t("driverPerformance.filter.searchDriver")}
          aria-label={t("driverPerformance.filter.searchDriver")}
        />

        {activeTab === "performance" && (
          <label className="dp-page__filter-check">
            <input
              type="checkbox"
              checked={metricsMissingOnly}
              onChange={(e) => setMetricsMissingOnly(e.target.checked)}
            />
            {t("driverPerformance.filter.metricsMissingOnly")}
          </label>
        )}

        {showDateFilters && (
          <>
            <label className="dp-page__filter-date" htmlFor="dp-range-start">
              {t("driverPerformance.filter.rangeStart")}:
            </label>
            <input
              id="dp-range-start"
              type="date"
              className="daily-schedule__date-input"
              value={rangeStart}
              min={minRangeStart}
              max={rangeEnd}
              onChange={(e) => handleRangeStartChange(e.target.value)}
            />
            <label className="dp-page__filter-date" htmlFor="dp-range-end">
              {t("driverPerformance.filter.rangeEnd")}:
            </label>
            <input
              id="dp-range-end"
              type="date"
              className="daily-schedule__date-input"
              value={rangeEnd}
              min={minRangeStart}
              max={yesterday}
              onChange={(e) => handleRangeEndChange(e.target.value)}
            />
          </>
        )}
      </div>

      <p className="dp-page__intro">{t("driverPerformance.intro")}</p>

      {data && !errorMessage && (
        <div className="dp-page__summary">
          <SummaryStat
            label={t("driverPerformance.summary.drivers")}
            value={`${formatNumber(summary.activeDrivers)} / ${formatNumber(summary.drivers)}`}
            hint={t("driverPerformance.summary.driversHint")}
          />
          <SummaryStat
            label={t("driverPerformance.summary.monthServices", { days: periodDays })}
            value={formatNumber(summary.monthServices)}
          />
          <SummaryStat
            label={t("driverPerformance.summary.monthKm", { days: periodDays })}
            value={formatKm(summary.monthKm)}
          />
          <SummaryStat
            label={t("driverPerformance.summary.monthTime", { days: periodDays })}
            value={formatMinutes(summary.monthMinutes)}
          />
        </div>
      )}

      {isLoading && !data && (
        <div className="dp-page__loading">
          <LoadingSpinner />
          <span>{t("driverPerformance.loading")}</span>
        </div>
      )}

      {errorMessage && <div className="dp-page__error">{errorMessage}</div>}

      {data && !errorMessage && (
        <>
          <div className="dp-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "performance"}
              className={`dp-tab${activeTab === "performance" ? " dp-tab--active" : ""}`}
              onClick={() => {
                setActiveTab("performance");
                setSelectedRestDriver(null);
              }}
            >
              {t("driverPerformance.section.performance")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "upcoming"}
              className={`dp-tab${activeTab === "upcoming" ? " dp-tab--active" : ""}`}
              onClick={() => {
                setActiveTab("upcoming");
                setSelectedRestDriver(null);
              }}
            >
              {t("driverPerformance.section.upcoming")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "rest"}
              className={`dp-tab${activeTab === "rest" ? " dp-tab--active" : ""}`}
              onClick={() => {
                setActiveTab("rest");
                setSelectedDriver(null);
              }}
            >
              {t("driverPerformance.section.rest")}
            </button>
          </div>

          {activeTab === "performance" && (
            <>
              <section className="dp-section">
                <div className="dp-section__head">
                  <span className="dp-section__hint">{t("driverPerformance.section.performanceHint")}</span>
                </div>
                <DriverStatsTable
                  rows={activePerformanceRows}
                  avgMonth={avgActiveMonth}
                  onSelectDriver={setSelectedDriver}
                  t={t}
                  periodDays={periodDays}
                />
              </section>

              {passivePerformanceRows.length > 0 && (
                <section className="dp-section">
                  <div className="dp-section__head">
                    <h3>{t("driverPerformance.section.passive")}</h3>
                    <span className="dp-section__hint">{t("driverPerformance.section.passiveHint")}</span>
                  </div>
                  <DriverStatsTable
                    rows={passivePerformanceRows}
                    avgMonth={avgActiveMonth}
                    onSelectDriver={setSelectedDriver}
                    t={t}
                    showLeaders={false}
                    periodDays={periodDays}
                  />
                </section>
              )}
            </>
          )}

          {activeTab === "upcoming" && (
            <section className="dp-section">
              <div className="dp-section__head">
                <span className="dp-section__hint">{t("driverPerformance.section.upcomingHint")}</span>
              </div>
              <UpcomingAgenda rows={upcomingActiveRows} onSelectDriver={setSelectedDriver} t={t} />
            </section>
          )}

          {activeTab === "rest" && (
            <>
              {restErrorMessage && <div className="dp-page__error">{restErrorMessage}</div>}

              {restLoading && !restData && !restErrorMessage && (
                <div className="dp-page__loading">
                  <LoadingSpinner />
                  <span>{t("driverPerformance.loading")}</span>
                </div>
              )}

              {restData && !restErrorMessage && (
                <>
                  <div className="dp-page__summary dp-rest-page__summary">
                    <SummaryStat
                      label={t("driverPerformance.rest.summary.driversWithViolations")}
                      value={formatNumber(filteredRestStats.driversWithViolations)}
                    />
                    <SummaryStat
                      label={t("driverPerformance.rest.summary.totalViolations")}
                      value={formatNumber(filteredRestStats.totalViolations)}
                    />
                  </div>

                  <section className="dp-section">
                    <div className="dp-section__head">
                      <span className="dp-section__hint">{t("driverPerformance.section.restHint")}</span>
                    </div>
                    <RestFleetTable rows={restActiveRows} onSelectDriver={setSelectedRestDriver} t={t} />
                  </section>

                  {restPassiveRows.length > 0 && (
                    <section className="dp-section">
                      <div className="dp-section__head">
                        <h3>{t("driverPerformance.section.passive")}</h3>
                        <span className="dp-section__hint">{t("driverPerformance.section.passiveHint")}</span>
                      </div>
                      <RestFleetTable rows={restPassiveRows} onSelectDriver={setSelectedRestDriver} t={t} />
                    </section>
                  )}
                </>
              )}
            </>
          )}

          {(activeTab === "performance" || activeTab === "upcoming") && (
            <div className="dp-legend">
              {KIND_ORDER.map((kind) => {
                const meta = KIND_META[kind];
                return (
                  <span key={kind} className="dp-legend__item">
                    <span
                      className="dp-legend__swatch"
                      style={{ backgroundColor: meta.bg, borderColor: meta.border }}
                    />
                    {t(meta.labelKey)}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}

      {isLoading && data && <LoadingSpinner asOverlay />}

      <DriverPerformanceDetailModal
        show={!!selectedDriver}
        driver={selectedDriver}
        range={detailRange}
        onClose={() => setSelectedDriver(null)}
      />

      <DriverRestDetailModal
        show={!!selectedRestDriver}
        driver={selectedRestDriver}
        range={restDetailRange}
        onClose={() => setSelectedRestDriver(null)}
      />
    </div>
  );
};

const SummaryStat = ({ label, value, hint }) => (
  <div className="dp-stat" title={hint || ""}>
    <span className="dp-stat__value">{value}</span>
    <span className="dp-stat__label">{label}</span>
  </div>
);

export default DriverPerformance;
